import React from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import { IconButton, StepConnector, Tooltip, Chip } from '@material-ui/core';
import { CheckCircleOutlineOutlined, PlayForWork, RadioButtonUncheckedOutlined, CloudUpload } from '@material-ui/icons';
import { DocGenerator } from 'utils';
import { format } from 'date-fns';
import ACCBTheme, { TEXT } from '../ACCBTheme';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  actionsContainer: {
    marginBottom: theme.spacing(2),
  },
  resetContainer: {
    padding: theme.spacing(3),
  },
}));

const StyledConnector = withStyles({
  // active: {
  //   '& $line': {
  //     borderColor: '#4A3BFF',
  //   },
  // },
  vertical: {
    padding: '0 0 6px',
    marginLeft: '0.7rem',
  },
  completed: {
    '& $line': {
      borderColor: ACCBTheme.palette.primary.main,
    },
  },
  line: {
    borderColor: '#eaeaf0',
    borderRadius: 1,
    borderLeftWidth: 3,
    margin: 0,
  },
})(StepConnector);

function StyledIcon(props) {
  const { active, completed } = props;
  return (
    <div>
      {completed ? <CheckCircleOutlineOutlined fontSize='medium' color='primary' /> : <RadioButtonUncheckedOutlined fontSize='medium' style={{ color: TEXT.muted }} />}
    </div>
  );
}

export default function ShowProgress({
  activeDispute,
  timeLines,
  IS_END_USER,
  profile = {},
  onOpenDirections = () => { },
}) {
  const classes = useStyles();

  const activeStep = activeDispute.status === 'closed' ? timeLines.length : timeLines.length - 1;

  const handleDownloadResponse = (profile_type, downloadKey, disputeId) => {
    DocGenerator(disputeId, profile_type, downloadKey);
  }

  function renderInline(text) {
    return text.split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
      seg.startsWith("**") && seg.endsWith("**") ? (
        <strong key={i} style={{ fontWeight: 700 }}>
          {seg.slice(2, -2)}
        </strong>
      ) : (
        <React.Fragment key={i}>{seg}</React.Fragment>
      )
    );
  }

  const getTimelineHeadingColor = (label) => {
    const hasProfiles = label?.profile_ids && label.profile_ids.length > 0;
    if (!hasProfiles) return "#000000DE";

    const isTargetUser = label.profile_ids.some(id => String(id) === String(profile?.id));
    const isPending = label?.status === "pending" && !label?.submitted;
    const isCompleted = label?.status === "completed" || label?.submitted;

    if (isTargetUser) {
      if (isPending) return "#D97706";
      if (isCompleted) return "#027A48";
    } else {
      if (isPending) return "#6b7280";
      if (isCompleted) return "#4b5563";
    }
    return "#000000DE";
  };

  const getCustomStepIcon = (label, index) => {
    const hasProfiles = label?.profile_ids && label.profile_ids.length > 0;
    const isCompleted = label?.status === "completed" || label?.submitted;
    const isPending = label?.status === "pending" && !label?.submitted;
    const isTargetUser = hasProfiles && label.profile_ids.some(id => String(id) === String(profile?.id));

    if (hasProfiles) {
      if (isCompleted) {
        return <CheckCircleOutlineOutlined fontSize="medium" style={{ color: TEXT.success }} />;
      }
      if (isPending) {
        if (isTargetUser) {
          return <RadioButtonUncheckedOutlined fontSize="medium" style={{ color: TEXT.warning }} />;
        } else {
          return <RadioButtonUncheckedOutlined fontSize="medium" style={{ color: TEXT.muted }} />;
        }
      }
    }

    // Default icon
    const stepCompleted = index <= activeStep;
    return stepCompleted ? (
      <CheckCircleOutlineOutlined fontSize="medium" color="primary" />
    ) : (
      <RadioButtonUncheckedOutlined fontSize="medium" style={{ color: TEXT.muted }} />
    );
  };

  const renderStatusChip = (label) => {
    const hasProfiles = label?.profile_ids && label.profile_ids.length > 0;
    if (!hasProfiles) return null;

    const isTargetUser = label.profile_ids.some(id => String(id) === String(profile?.id));
    const isPending = label?.status === "pending" && !label?.submitted;
    const isCompleted = label?.status === "completed" || label?.submitted;

    if (isCompleted) {
      return (
        <Chip
          label="Submitted"
          size="small"
          style={{
            backgroundColor: isTargetUser ? "#ECFDF3" : "#F3F4F6",
            color: isTargetUser ? "#027A48" : "#4b5563",
            fontWeight: 500,
            fontSize: "0.75rem",
            borderRadius: "4px",
            height: "20px",
            marginLeft: "8px",
          }}
        />
      );
    }

    if (isPending) {
      return (
        <Chip
          label={isTargetUser ? "Action Required" : "Pending"}
          size="small"
          style={{
            backgroundColor: isTargetUser ? "#FEF3C7" : "#F3F4F6",
            color: isTargetUser ? "#D97706" : "#4b5563",
            fontWeight: 500,
            fontSize: "0.75rem",
            borderRadius: "4px",
            height: "20px",
            marginLeft: "8px",
          }}
        />
      );
    }

    return null;
  };

  return (
    <div className={classes.root}>
      <Stepper activeStep={activeStep} orientation="vertical" connector={<StyledConnector />} style={{ padding: "12px" }}>
        {timeLines.map((label, index) => {
          const hasProfiles = label?.profile_ids && label.profile_ids.length > 0;
          const isTargetUser = hasProfiles && label.profile_ids.some(id => String(id) === String(profile?.id));
          const isPending = label?.status === "pending" && !label?.submitted;

          return (
            <Step key={label?.id}>
              <StepLabel icon={getCustomStepIcon(label, index)}>
                <span style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "0.875rem", color: getTimelineHeadingColor(label) }}>
                    {renderInline(label?.heading)}
                  </span>

                  {label?.download && !IS_END_USER && (
                    <Tooltip title="Download Response" placement="bottom">
                      <IconButton
                        onClick={() => handleDownloadResponse(label?.download, label?.download_key, activeDispute?.id)}
                        style={{ padding: 0 }}
                      >
                        <PlayForWork color="primary" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {renderStatusChip(label)}
                </span>
                <span style={{ fontSize: "0.8rem", color: TEXT.muted, display: "inline-flex", alignItems: "center" }}>
                  {format(new Date(label?.created_at), "dd/MM/yyyy | h:mm a")}

                  {isTargetUser && isPending && (
                    <Tooltip title="Upload Document for Direction" placement="bottom">
                      <span style={{ display: "inline-flex" }}>
                        <IconButton
                          onClick={onOpenDirections}
                          style={{ padding: 0, marginLeft: "8px" }}
                        >
                          <CloudUpload style={{ color: TEXT.warning, fontSize: "1.2rem" }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </span>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </div>
  );
}
